import {EntitySchemaService} from '@catcore/services/entity-schema.service';
import {resolveEntityNameByResource} from '@catcore/utils';
import {resolveLabelForElementName} from '@catfeatures/shared/utils';
import {Resource} from '@catfeatures/types';
import {ExplorerItem} from '@catui/types';
import {isNullOrUndefined} from 'util';

/**
 * This factory provides methods to create tree containing of NavItem-items out of an
 * "JSON Hal"-formatted JSON-object.
 *
 * As starting point either a object with linked child-resources can be provided, or a list or items
 */
export class JsonHalToExplorerTreeFactory {
  constructor(private schemaService: EntitySchemaService) {
  }

  getForRoot(rootSource: any, options: Options, filterKeys: string[] = [], sortByLabel: boolean = true): ExplorerItem[] {
    let result;

    if (Array.isArray(rootSource)) {
      result = this.getForList(rootSource, options, filterKeys, sortByLabel);
    } else {
      result = this.getForRefs(rootSource, options, filterKeys);
    }
    return result;
  }

  getForList(items: ResourceCollectionItem[], options: Options, filterKeys: string[] = [], sortByLabel: boolean = true): ExplorerItem[] {
    return buildListFromCollection(items, options, filterKeys, sortByLabel);
  }

  /**
   * Creates a list of items related to the provided Resource.
   * The list ist a concationation of possible available linked 'action'- and natural resources
   * @param {Resource} resource
   * @param {Options} options
   * @param string[] filterKeys List of entity schema properties to ignore
   * @returns {ExplorerItem[]}
   */
  getForRefs(resource: Resource, options: Options, filterKeys: string[] = []): ExplorerItem[] {
    const entityName = resolveEntityNameByResource(resource);
    const entitySchema = this.schemaService.getForEntityName(entityName);
    // apply filter on properties
    filterKeys.forEach((filter, idx) => {
      if (entitySchema.properties.hasOwnProperty(filter)) {
        delete entitySchema.properties[filter];
      }
    });

    let mappedItems = buildForActions(resource._links, filterKeys);
    mappedItems = mergeMap(mappedItems, buildBySchema(entitySchema, resource._links, []));
    const explorerItems = mapToExplorerItems(mappedItems, resource, options, filterKeys);
    return explorerItems;
  }

  buildListItem(item: ResourceCollectionItem, options): ExplorerItem {
    return buildExplorerItem(item, options);
  }
}

export interface NavItem {
  name: string;
  ref: string;
  couldHaveChildren: boolean;
  hasChildren: boolean;
  resourceInfo: any;
  selfHref: string;
}

export interface ResourceCollectionItem {
  name: string;
  _links: { self: { href: string } };
}

export interface Options {
  label: (index: number) => string;
  routeTargets: { forRootNode: string, forChildNode: string };
}

class ResourceExplorerItem implements ExplorerItem {
  readonly resourceId: string;

  constructor(public label, public value, public children, public expanded, public allowedActions = [], public filter: string[] = []) {
    this.resourceId = value.route.resourceId;
  }

  equals(target: ExplorerItem): boolean {
    return this.resourceId === target.value.route.resourceId;
  }
}

const mapToExplorerItems = (propertyToValues: Map<string, NavItem>, resource: Resource, options: Options, filterKeys: string[] = []): ExplorerItem[] => {
  const childNodes: ExplorerItem[] = [];

  propertyToValues.forEach((currentNavItem: NavItem) => {
    const resourceInfo = currentNavItem.resourceInfo;
    const childHasChildren = currentNavItem.hasChildren;
    const couldHaveChildren = currentNavItem.couldHaveChildren;

    const label = resolveLabelForElementName(currentNavItem.name, childHasChildren);
    const itemLabel = resolveLabelForElementName(currentNavItem.name, false);
    const customOptions = {...options, label: () => itemLabel};
    let nodeChildren = childHasChildren ? buildListFromCollection(resourceInfo, customOptions, filterKeys) : null;
    const shouldBeRouted = !isNullOrUndefined(currentNavItem.selfHref);
    const routeCommand = couldHaveChildren ? options.routeTargets.forRootNode : this.options.routeTargets.forChildNode;

    nodeChildren = !nodeChildren && couldHaveChildren ? [] : nodeChildren;

    const value = {
      navItem: currentNavItem,
      route: {
        command: shouldBeRouted ? routeCommand : null,
        resourceId: currentNavItem.selfHref,
        parentResourceId: resource._links.self.href,
      },
    };

    childNodes.push(new ResourceExplorerItem(label, value, nodeChildren, false, [], filterKeys));
  });
  return childNodes;
};

const buildListFromCollection = (items: ResourceCollectionItem[], options: Options, filterKeys: string[] = [], sortByLabel: boolean = true): ExplorerItem[] => {
  const list: ExplorerItem[] = [];
  items.forEach((resourceItem, index) => {
    const item = buildExplorerItem(resourceItem, options, index, filterKeys);

    if (item) {
      list.push(item);
    }
  });
  if (sortByLabel) {
    return sortListByLabel(list);
  } else {
    return list;
  }
};

const buildExplorerItem = (item: ResourceCollectionItem, options: Options, indexInOriginList = 0, filterKeys: string[] = []) => {
  const itemLabel = buildLabelForItem(item, options.label(indexInOriginList), indexInOriginList);
  const command = options.routeTargets.forChildNode;
  const resourceId = findHrefForListRef(item);
  const parentResourceId = null;

  filterKeys.forEach((filter, idx) => {
    if (itemLabel.indexOf(filter) !== -1) {
      return null;
    }
  });

  return {
    label: itemLabel,
    value: {
      route: {
        command,
        resourceId,
        parentResourceId,
      },
    },
    expanded: false,
    allowedActions: [],
    filter: filterKeys,
  };
};

const sortListByLabel = (list: ExplorerItem[]): ExplorerItem[] => {
  return list.sort((first: ExplorerItem, second: ExplorerItem) => {
    return first.label.localeCompare(second.label, undefined, {numeric: true, sensitivity: 'base'});
  });
};

const findHrefForListRef = (ref: any) => {
  let href = null;
  let hasLinkedSelf = !isNullOrUndefined(ref._links);
  hasLinkedSelf = hasLinkedSelf && !isNullOrUndefined(ref._links.self);

  const hasEmbeddedHref = !isNullOrUndefined(ref.href);

  if (hasLinkedSelf) {
    href = ref._links.self.href;
  } else if (hasEmbeddedHref) {
    href = ref.href;
  }

  return href;
};

const buildBySchema = (entitySchema: any, linkedResources: any, includeNames: string[]): Map<string, NavItem> => {
  const propertyNames = Object.keys(entitySchema.properties);
  const linkedResourceNames = Object.keys(linkedResources);
  const resultMap: Map<string, NavItem> = new Map();

  propertyNames.forEach((entityPropertyName) => {
    const entityProperty = entitySchema.properties[entityPropertyName];
    if (entityProperty.type === 'array') {
      const linkedResource = linkedResourceNames.includes(entityPropertyName) ? linkedResources[entityPropertyName] : null;
      const hasChildren = linkedResource !== null && linkedResource.children !== null;
      const selfHref = entityProperty.resourceUri;
      resultMap.set(entityPropertyName, {
        name: entityPropertyName,
        ref: entityPropertyName,
        couldHaveChildren: true,
        resourceInfo: linkedResource,
        hasChildren,
        selfHref,
      });
    }
  });

  return resultMap;
};

const isActionNamed = (name: string): boolean => {
  return name.match(/^(action:)/) !== null;
};

const propertyNameFromActionName = (propertyName: string) => {
  return toPropertyNameStyle(propertyName.replace('action:', ''));
};

const isActionItemCollection = (actionInfo: any) => {
  return isNullOrUndefined(actionInfo.href);
};

const getItemPropertiesForLazyLoadedAction = (actionInfo: { href: string }) => {
  return {
    couldHaveChildren: true,
    resourceInfo: {},
    hasChildren: false,
    selfHref: actionInfo.href,
  };
};

const getItemPropertiesForEagerLoadedAction = (actionInfo: string[]) => {
  return {
    couldHaveChildren: true,
    resourceInfo: actionInfo,
    hasChildren: true,
    selfHref: null,
  };
};

const buildForActions = (childsByActions, filterKeys: string[] = []): Map<string, NavItem> => {
  const resultMap: Map<string, NavItem> = new Map();
  Object
    .keys(childsByActions)
    .filter((propertyName) => {
      return isActionNamed(propertyName) && !filterKeys.includes(propertyName);
    })
    .forEach((actionName) => {
      const propertyName = propertyNameFromActionName(actionName);
      const actionInfo = childsByActions[actionName];
      const propertyLabel = resolveLabelForElementName(propertyName, true);
      let itemProperties = null;

      if (isActionItemCollection(actionInfo)) {
        itemProperties = getItemPropertiesForEagerLoadedAction(actionInfo);
      } else {
        itemProperties = getItemPropertiesForLazyLoadedAction(actionInfo);
      }

      resultMap.set(
        propertyLabel,
        Object.assign({}, {name: propertyLabel, ref: propertyName}, itemProperties),
      );
    })
  ;
  return resultMap;

};

const buildLabelForItem = (linkedResourceItem, labelPrefix, itemPosition): string => {

  let label = null;
  const lookupPropertyNames = ['name', 'label'];
  const resourceKeyNames = Object.keys(linkedResourceItem);

  do {
    const lookupName = lookupPropertyNames.pop();
    if (resourceKeyNames.includes(lookupName)) {
      label = linkedResourceItem[lookupName];
    }

  } while (lookupPropertyNames.length > 0 && isNullOrUndefined(label));

  if (isNullOrUndefined(label)) {
    label = `${labelPrefix} ${itemPosition + 1}`;
  }

  return label;
};

const mergeMap = (source: Map<any, any>, additional: Map<any, any>) => {
  return new Map([...Array.from(source), ...Array.from(additional)]);
};

export const toPropertyNameStyle = (name: string): string => {
  return name.replace(/-/g, '_');
};



// WEBPACK FOOTER //
// /agent/workspace/CUS-LHT_NICE_nto_nto_CAT_release@2/app/Resources/Public/src/app/modules/features/shared/resource-navigation-factory.ts